import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableFooter from "components/common/advance-table/AdvanceTableFooter";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React, { useState } from "react";
import { Alert, Dropdown, Spinner } from "react-bootstrap";
import { ApiKeyDetailsModal } from "./ApiKeysDetailsModal";
import useApiKeys from "hooks/apikeys/useApiKeys";
import { ApiKeyUpdateModal } from "./ApiKeyUpdateModal";
import { toast } from "react-toastify";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import CardDropdown from "components/common/CardDropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SubtleBadge from "components/common/SubtleBadge";
import useFormattedDate from "hooks/useFormatDate";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";

export const ApiKeys = () => {
  const token = sessionStorage.getItem("authToken");

  const {
    apiKeys,
    loading,
    fetchApiKeyById,
    selectedKey,
    setSelectedKey,
    updateApiKey,
    deleteApiKey,
    error,
  } = useApiKeys(token);

  const [showModal, setShowModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);

  const handleViewApiKey = async (id) => {
    try {
      await fetchApiKeyById(id);
      setShowModal(true);
    } catch (error) {
      toast.error(error, { theme: "colored" });
    }
  };

  const handleUpdateApiKey = async (id) => {
    try {
      await fetchApiKeyById(id);
      setShowUpdateModal(true);
    } catch (error) {
      toast.error(error, { theme: "colored" });
    }
  };

  const confirmDelete = (id) => {
    setKeyToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    await deleteApiKey(keyToDelete);
  }

  const formatKey = (key) => `${key.slice(0, 6)}...${key.slice(-4)}`;
  const {formatDateTime} = useFormattedDate();
  const formatText = (text) => `${text.slice(0, 15)}...`;

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "clientId",
      header: "Client ID",
      cell: ({ getValue }) => formatKey(getValue()),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "key",
      header: "Key",
      cell: ({ getValue }) => formatKey(getValue()),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "active",
      header: "Status",
      cell: ({ getValue }) => {
        const value = getValue();
        const type = value == true ? "success" : "secondary";
        const content = value == true ? "Active" : "Disabled";
        const icon = value == true ? "check" : "ban";
        return (
          <SubtleBadge pill bg={type}>
            {content}
            <FontAwesomeIcon
              icon={icon}
              transform="shrink-2"
              className="ms-1"
            />
          </SubtleBadge>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "fs-9" },
      },
    },
    {
      accessorKey: "expiresAt",
      header: "Expires At",
      cell: ({ getValue }) => formatDateTime(getValue()),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ getValue }) => formatText(getValue()),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <CardDropdown drop="start">
            <div className="py-2">
              <Dropdown.Item onClick={() => handleViewApiKey(item.id)}>
                View
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleUpdateApiKey(item.id)}>
                Edit
              </Dropdown.Item>
              <Dropdown.Divider as="div" />
              <Dropdown.Item
                onClick={() => confirmDelete(item.id)}
                className="text-danger"
              >
                Delete
              </Dropdown.Item>
            </div>
          </CardDropdown>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
    },
  ];

  const table = useAdvanceTable({
    data: apiKeys,
    columns,
    sortable: true,
    selection: true,
    pagination: true,
    perPage: 10,
  });

  if (loading && apiKeys.length === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading style={{ fontSize: 16 }}>
          <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
          Error
        </Alert.Heading>
        <p style={{ fontSize: "13.33px" }}>{error}</p>
      </Alert>
    );
  }

  return (
    <>
      <WidgetSectionTitle
        icon="layer-group"
        title="API Keys"
        subtitle="API Keys from the server for monitoring and management."
        transform="shrink-2"
        className="mb-4 mt-3"
      />

      <AdvanceTableProvider {...table}>
        <AdvanceTable
          headerClassName="bg-300 text-nowrap align-middle"
          rowClassName="align-middle white-space-nowrap"
          tableProps={{
            bordered: true,
            striped: true,
            className: "fs-10 mb-0 overflow-hidden",
          }}
        />
        <div className="mt-3">
          <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
        </div>
      </AdvanceTableProvider>

      {/* Modal */}
      <ApiKeyDetailsModal
        selectedKey={selectedKey}
        loading={loading}
        showModal={showModal}
        setShowModal={setShowModal}
      />

      <ApiKeyUpdateModal
        selectedKey={selectedKey}
        loading={loading}
        showUpdateModal={showUpdateModal}
        setShowUpdateModal={setShowUpdateModal}
        updateApiKey={updateApiKey}
      />

      <ConfirmDeleteModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirm={handleDelete}
        itemName="api key"
        loading={loading}       
      />
    </>
  );
};
